import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";
import { Upload, FileCheck } from "lucide-react";
import { uploadToIPFS, submitInvoice } from "../utils/api";
import LoadingModal from "../components/LoadingModal";

const AddInvoice = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    status: "uploading",
    message: "",
  });

  const transactionTypes = ["Purchase", "Sale", "Expense", "Income"];
  const paymentStatuses = ["Paid", "Pending", "Partial"];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      toast.success(`File selected: ${file.name}`);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      toast.success(`File selected: ${file.name}`);
    }
  };

  const onSubmit = async (data) => {
    try {
      let ipfsHash = "";

      // Upload file to IPFS if selected
      if (selectedFile) {
        setModalState({
          isOpen: true,
          status: "uploading",
          message: "Uploading document to IPFS... This may take a moment.",
        });

        const uploadResult = await uploadToIPFS(selectedFile);
        ipfsHash = uploadResult.data.ipfsHash;

        toast.success("Document uploaded to IPFS successfully!");
      }

      // Submit to blockchain
      setModalState({
        isOpen: true,
        status: "submitting",
        message:
          "Submitting invoice to blockchain... Please wait for confirmation.",
      });

      const invoiceData = {
        invoiceNumber: data.invoiceNumber,
        companyName: data.companyName || "",
        amount: data.amount,
        transactionType: data.transactionType || "",
        category: data.category || "",
        documentHash: ipfsHash,
        financialYear: data.financialYear,
        taxNumber: data.taxNumber || "",
        paymentStatus: data.paymentStatus || "",
        notes: data.notes || "",
      };

      const result = await submitInvoice(invoiceData);

      setModalState({
        isOpen: true,
        status: "success",
        message: `Invoice ${data.invoiceNumber} has been successfully recorded on the blockchain!`,
      });

      // Reset form
      reset();
      setSelectedFile(null);
    } catch (error) {
      console.error("Submission error:", error);
      setModalState({
        isOpen: true,
        status: "error",
        message:
          error.response?.data?.error ||
          error.message ||
          "Failed to submit invoice",
      });
    }
  };

  const closeModal = () => {
    setModalState({ ...modalState, isOpen: false });
  };

  return (
    <div className="add-invoice-page">
      <Toaster position="top-right" />
      <LoadingModal
        isOpen={modalState.isOpen}
        status={modalState.status}
        message={modalState.message}
        onClose={closeModal}
      />

      <motion.div
        className="form-container"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="form-header">
          <h1 className="form-title">Add New Invoice</h1>
          <p className="form-subtitle">
            Submit invoice records to the blockchain
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="invoice-form">
          {/* Mandatory Fields Section */}
          <div className="form-section">
            <h3 className="section-title">Mandatory Fields</h3>

            <div className="form-group">
              <label className="form-label">
                Invoice Number <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="INV-2024-001"
                {...register("invoiceNumber", {
                  required: "Invoice number is required",
                  minLength: { value: 3, message: "Minimum 3 characters" },
                })}
              />
              {errors.invoiceNumber && (
                <span className="error-message">
                  {errors.invoiceNumber.message}
                </span>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Amount <span className="required">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  placeholder="10000"
                  {...register("amount", {
                    required: "Amount is required",
                    min: { value: 1, message: "Amount must be greater than 0" },
                  })}
                />
                {errors.amount && (
                  <span className="error-message">{errors.amount.message}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Financial Year <span className="required">*</span>
                </label>
                <select
                  className="form-input"
                  defaultValue={currentYear}
                  {...register("financialYear", {
                    required: "Financial year is required",
                  })}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                {errors.financialYear && (
                  <span className="error-message">
                    {errors.financialYear.message}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Optional Fields Section */}
          <div className="form-section">
            <h3 className="section-title">Optional Fields</h3>

            <div className="form-group">
              <label className="form-label">Company Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="ABC Corporation Pvt Ltd"
                {...register("companyName")}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Transaction Type</label>
                <select className="form-input" {...register("transactionType")}>
                  <option value="">Select type</option>
                  {transactionTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Category</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Office Supplies"
                  {...register("category")}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Tax Number (GST/VAT)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="22AAAAA0000A1Z5"
                  {...register("taxNumber")}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Payment Status</label>
                <select className="form-input" {...register("paymentStatus")}>
                  <option value="">Select status</option>
                  {paymentStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-input"
                rows="3"
                placeholder="Additional information..."
                {...register("notes")}
              />
            </div>

            {/* File Upload */}
            <div className="form-group">
              <label className="form-label">Upload Document (Optional)</label>
              <div
                className={`file-upload-area ${isDragging ? "dragging" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById("file-input").click()}
              >
                <input
                  id="file-input"
                  type="file"
                  hidden
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                {selectedFile ? (
                  <div className="file-selected">
                    <FileCheck size={48} />
                    <p className="file-name">{selectedFile.name}</p>
                    <p className="file-size">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                ) : (
                  <div className="file-placeholder">
                    <Upload size={48} />
                    <p>Drag & drop or click to upload</p>
                    <p className="file-hint">
                      PDF, DOC, DOCX, JPG, PNG (Max 10MB)
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-reset"
              onClick={() => {
                reset();
                setSelectedFile(null);
              }}
            >
              Reset Form
            </button>
            <button type="submit" className="btn-submit">
              Submit to Blockchain
            </button>
          </div>
        </form>
      </motion.div>

      <style jsx="true">{`
        .add-invoice-page {
          min-height: calc(100vh - 80px);
          padding: 40px 24px;
        }

        .form-container {
          max-width: 900px;
          margin: 0 auto;
          background: white;
          border-radius: 24px;
          padding: 48px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
        }

        .form-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .form-title {
          font-size: 36px;
          font-weight: 800;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 12px;
        }

        .form-subtitle {
          font-size: 16px;
          color: #64748b;
        }

        .invoice-form {
          display: flex;
          flex-direction: column;
          gap: 32px;
        }

        .form-section {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .section-title {
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          padding-bottom: 12px;
          border-bottom: 2px solid #f1f5f9;
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-label {
          font-size: 14px;
          font-weight: 600;
          color: #475569;
        }

        .required {
          color: #ef4444;
        }

        .form-input {
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 15px;
          font-family: "Inter", sans-serif;
          transition: all 0.3s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .error-message {
          font-size: 13px;
          color: #ef4444;
          font-weight: 500;
        }

        .file-upload-area {
          border: 2px dashed #cbd5e1;
          border-radius: 12px;
          padding: 40px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          background: #f8fafc;
        }

        .file-upload-area:hover {
          border-color: #667eea;
          background: #f1f5f9;
        }

        .file-upload-area.dragging {
          border-color: #667eea;
          background: #ede9fe;
        }

        .file-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          color: #64748b;
        }

        .file-hint {
          font-size: 13px;
          color: #94a3b8;
        }

        .file-selected {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          color: #667eea;
        }

        .file-name {
          font-weight: 600;
          color: #1e293b;
        }

        .file-size {
          font-size: 13px;
          color: #64748b;
        }

        .form-actions {
          display: flex;
          gap: 16px;
          padding-top: 24px;
          border-top: 2px solid #f1f5f9;
        }

        .btn-reset {
          flex: 1;
          padding: 14px;
          border: 2px solid #e2e8f0;
          background: white;
          color: #64748b;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-reset:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
        }

        .btn-submit {
          flex: 2;
          padding: 14px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-submit:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
        }

        @media (max-width: 768px) {
          .form-container {
            padding: 32px 24px;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .form-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default AddInvoice;
